import { selectManagerAndDirectReportsByUserAccountId } from "app/containers/AdvancedCsvUpload/slice";
import { selectTeamsByTeamId } from "app/containers/Global/slice";
import WarningModal from "app/storybookComponents/Modals/WarningModal";
import { getS } from "utils/helperFunctions";
import { useAppSelector } from "utils/redux/hooks";
import { useMemo, useCallback } from "react";
import {
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
} from "../slice";
import {
  ManagerAndDirectReports,
  ManagerRelationship,
} from "app/containers/AdvancedCsvUpload/types";
import { ManagerCustomListItem } from "../types";
import useModalContent from "utils/hooks/useModalContent";

interface Props {
  isOpen: boolean;
  onHide: () => void;
  onConfirmClick: () => void;
  reportType?: "seniorManager" | "teamLeader";
  valueChangingTo?: boolean;
}

const EmailReportToggleWarning = ({
  isOpen,
  onHide,
  onConfirmClick,
  reportType,
  valueChangingTo,
}: Readonly<Props>) => {
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );
  const companyWeeklyCheckInCustomList = useAppSelector(
    selectWeeklyCheckInCustomList
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const teams = useAppSelector(selectTeamsByTeamId);

  // Use the custom hook to freeze modal content
  const frozenReportType = useModalContent(reportType, isOpen);
  const frozenValueChangingTo = useModalContent(valueChangingTo, isOpen);

  const getReportTypeText = (
    type = frozenReportType,
    toLower: boolean = false
  ) => {
    if (type === "seniorManager") {
      return toLower ? "senior manager" : "Senior Manager";
    }

    if (type === "teamLeader") {
      return toLower ? "team leader" : "Team Leader";
    }
  };

  const managerMap = useMemo(() => {
    const managerMap: Record<number, ManagerAndDirectReports> = {};
    Object.values(managersAndDirectReportsByUserAccountId).forEach((report) => {
      if (!report?.directReportRelationships?.length) {
        return;
      }
      const managerAccountId =
        report?.directReportRelationships[0]?.managerUserAccountId;
      managerMap[managerAccountId] = report;
    });
    return managerMap;
  }, [managersAndDirectReportsByUserAccountId]);

  const areAnyDirectReportsManagers = useCallback(
    (directReportIds: number[]) =>
      directReportIds.some((drR) => !!managerMap[drR]),
    [managerMap]
  );
  const getDirectReportIds = (managerRelationships: ManagerRelationship[]) =>
    managerRelationships.map((drR) => drR.employeeUserAccountId);

  // Gets the count of senior managers in the custom list
  const getSeniorManagerCountForCustomList = useCallback(() => {
    // Map to track senior managers
    const seniorManagerMap: Record<number, { isSeniorManager: boolean }> = {};
    const checkedManagerIds = new Set();

    const getSeniorManagerCountRecursive = (
      userAccountIds: (number | ManagerCustomListItem)[],
      parentManagerId?: number | null,
      isEntireReportingStructure?: boolean
    ) => {
      // This is here to prevent infinite recursion
      if (parentManagerId) {
        checkedManagerIds.add(parentManagerId);
      }

      userAccountIds.forEach((user) => {
        let trueIsEntireReportingStructure = isEntireReportingStructure;
        const currentUserAccountId =
          typeof user === "number" ? user : user.userAccountId;

        if (typeof user !== "number") {
          trueIsEntireReportingStructure = user.entireReportingStructure;
        }
        const directReportsOfDirectReports =
          managerMap?.[currentUserAccountId]?.directReportRelationships;

        // If we already checked this user no need to proceed:
        if (
          checkedManagerIds.has(currentUserAccountId) || // If we already checked this user, no need to check again
          !directReportsOfDirectReports // If no direct reports, no need to do anything else
        ) {
          return;
        }

        // Mark parent as senior manager if their direct reports are also managers
        if (parentManagerId && !seniorManagerMap[parentManagerId]) {
          seniorManagerMap[parentManagerId] = { isSeniorManager: true };
        }

        const directReportIds = getDirectReportIds(
          directReportsOfDirectReports
        );
        // If entire reporting structure is enabled, then we need to check the direct reports of the current user
        if (trueIsEntireReportingStructure) {
          getSeniorManagerCountRecursive(
            directReportIds,
            currentUserAccountId,
            trueIsEntireReportingStructure
          );
          // If trueIsEntireReportingStructure is not enabled all we have to do is check if any of the direct reports are managers
        } else if (areAnyDirectReportsManagers(directReportIds)) {
          seniorManagerMap[currentUserAccountId] = { isSeniorManager: true };
        }
      });

      return;
    };

    // Start the recursive process with the managers from the custom list
    getSeniorManagerCountRecursive(
      companyWeeklyCheckInCustomList?.managers ?? [],
      null
    );

    const count = Object.values(seniorManagerMap).reduce(
      (acc, val) => acc + (val.isSeniorManager ? 1 : 0),
      0
    );

    return count;
  }, [managerMap, companyWeeklyCheckInCustomList, areAnyDirectReportsManagers]);

  const getSeniorManagerCount = useCallback(() => {
    if (
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return getSeniorManagerCountForCustomList();
    }

    const seniorManagerIds: number[] = [];
    Object.entries(managerMap).forEach(([managerAccountId, report]) => {
      if (!report?.directReportRelationships?.length) {
        return;
      }

      if (
        report?.directReportRelationships?.some(
          (r) => !!managerMap[r.employeeUserAccountId]
        )
      ) {
        seniorManagerIds.push(Number(managerAccountId));
      }
    });

    return new Set(seniorManagerIds).size;
  }, [
    managerMap,
    weeklyCheckInCompanySettings,
    getSeniorManagerCountForCustomList,
  ]);

  const getTeamLeaderCount = useCallback(() => {
    const teamLeaderArray: number[] = [];

    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      Object.values(teams).forEach((team) => {
        if (team?.teamLeadUserAccountIds?.length) {
          teamLeaderArray.push(...team.teamLeadUserAccountIds);
        }
      });
    } else {
      companyWeeklyCheckInCustomList?.teams.forEach((teamId) => {
        const teamObj = teams[teamId];
        if (teamObj?.teamLeadUserAccountIds?.length) {
          teamLeaderArray.push(...teamObj.teamLeadUserAccountIds);
        }
      });
    }
    const uniqueTeamLeaders = new Set(teamLeaderArray);
    return uniqueTeamLeaders.size;
  }, [weeklyCheckInCompanySettings, companyWeeklyCheckInCustomList, teams]);

  const count = useMemo(() => {
    if (frozenReportType === "seniorManager") {
      return getSeniorManagerCount();
    }

    if (frozenReportType === "teamLeader") {
      return getTeamLeaderCount();
    }

    return 0;
  }, [frozenReportType, getSeniorManagerCount, getTeamLeaderCount]);

  const getWarningMessage = () => {
    if (!frozenValueChangingTo) {
      return "Email reports will no longer be sent after each check-in period.";
    }
    if (frozenReportType === "seniorManager") {
      return "An email will be sent after each check-in period with key patterns, recommended actions, and a completion analysis based on check-ins completed by the people in their span of control.";
    }
    if (frozenReportType === "teamLeader") {
      return "An email with team patterns, recommended actions, and completion analysis will be sent after each check-in period.";
    }
    return "";
  };

  const getAdditionalInformation = () => {
    if (!frozenValueChangingTo) {
      return null;
    }
    if (frozenReportType === "seniorManager") {
      return (
        <div className="email-report-toggle-warning-info">
          <p>
            Reports are sent when "Development-focused" privacy is selected and
            any of these apply:
          </p>
          <ul>
            <li>Check-ins are enabled for that manager</li>
            <li>
              Check-ins are enabled for the entire reporting structure of a
              senior manager above them
            </li>
            <li>Check-ins are enabled org-wide</li>
          </ul>
        </div>
      );
    }
    if (frozenReportType === "teamLeader") {
      return (
        <div className="email-report-toggle-warning-info">
          <p>Reports are sent to team leaders when:</p>
          <ul>
            <li>Check-ins are enabled for their team</li>
            <li>Check-ins are enabled org-wide</li>
          </ul>
        </div>
      );
    }
    return null;
  };

  const getWarningTitle = () => {
    if (frozenValueChangingTo) {
      return `Currently ${count} ${getReportTypeText(frozenReportType)}${getS(
        count
      )} will receive this report`;
    }

    return `Currently ${count} ${getReportTypeText(frozenReportType)}${getS(
      count
    )} receive${count === 1 ? "s" : ""} this report`;
  };

  const enableOrDisable = frozenValueChangingTo ? "Enable" : "Disable";
  return (
    <WarningModal
      includeHeaderLine
      modalTitle={`${enableOrDisable} Weekly ${getReportTypeText(
        frozenReportType
      )} Report`}
      warningTitle={getWarningTitle()}
      warningMessage={getWarningMessage()}
      extraBody={getAdditionalInformation()}
      hideModal={onHide}
      onConfirmClick={onConfirmClick}
      isOpen={isOpen}
      isDanger={false}
      cancelButtonText="Back"
      customButtonText={`Confirm and ${enableOrDisable.toLowerCase()}`}
    />
  );
};

export default EmailReportToggleWarning;
